import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixMultilingualLocalesV2LocalesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/locales',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveWixMultilingualLocalesV2SupportedLocalesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/locales',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/locales',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_multilingual_locales';

/**
 * Retrieves a list of all Wix-supported locales that can be added to a site.
 *
 * Call this method to display supported language details when creating or updating a locale.
 */
export function listSupportedLocales(
  payload: object
): RequestOptionsFactory<any> {
  function __listSupportedLocales({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'GET' as any,
      methodFqn:
        'wix.multilingual.locales.v2.SupportedLocalesService.ListSupportedLocales',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2SupportedLocalesServiceUrl({
        protoPath: '/v2/locales/supported',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listSupportedLocales;
}

/**
 * Creates a new secondary locale.
 *
 * This method adds a new secondary locale to the site. A site can have up to 200 locales and up to 100 visible locales.
 *
 * To retrieve a list of all locales supported by Wix that can be created on a site, call List Supported Locales.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * - This method can only be called on sites where multilingual mode is enabled.
 * - This method creates only secondary locales. To create and set a new primary locale, call Create New Primary Locale.
 *
 * </blockquote>
 */
export function createLocale(payload: object): RequestOptionsFactory<any> {
  function __createLocale({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'locale.createdDate' }, { path: 'locale.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'POST' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.CreateLocale',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'locale.createdDate' },
              { path: 'locale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createLocale;
}

/** Retrieves a locale by ID. */
export function getLocale(payload: object): RequestOptionsFactory<any> {
  function __getLocale({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'GET' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.GetLocale',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/{localeId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'locale.createdDate' },
              { path: 'locale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLocale;
}

/** Updates a locale by ID. */
export function updateLocale(payload: object): RequestOptionsFactory<any> {
  function __updateLocale({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'locale.createdDate' }, { path: 'locale.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'PATCH' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.UpdateLocale',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/{locale.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'locale.createdDate' },
              { path: 'locale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLocale;
}

/**
 * Deletes a locale by ID.
 *
 * This method removes only secondary locales from a site. You can't call this method to delete the primary locale or a locale that's set as the visitor primary locale.
 *
 * <blockquote class="warning">
 * <strong>Warning:</strong>
 *
 * Deleting a locale permanently removes it and all its associated data from the site.
 *
 * </blockquote>
 *
 * <blockquote class="tip">
 * <strong>Tip:</strong>
 *
 * - To change the primary locale, call Create New Primary Locale. This method automatically deletes the original primary locale and transfers its data to the new primary locale.
 * - To delete a locale that's set as the visitor primary locale, first call Set Visitor Primary Locale to change the visitor primary locale, then call this method.
 *
 * </blockquote>
 */
export function deleteLocale(payload: object): RequestOptionsFactory<any> {
  function __deleteLocale({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'DELETE' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.DeleteLocale',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/{localeId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteLocale;
}

/**
 * Retrieves a list of the site's locales, given the provided paging, filtering, and sorting. Up to 200 locales can be returned per request.
 *
 * The default `sort` is `locale.id` in `ASC`.
 *
 * To learn how to query projects, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language).
 */
export function queryLocales(payload: object): RequestOptionsFactory<any> {
  function __queryLocales({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'GET' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.QueryLocales',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'locales.createdDate' },
              { path: 'locales.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
            protoPath: '/v2/locale/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryLocales;
}

/**
 * Creates multiple secondary locales.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * - This method can only be called on sites where multilingual mode is enabled.
 *
 * </blockquote>
 */
export function bulkCreateLocales(payload: object): RequestOptionsFactory<any> {
  function __bulkCreateLocales({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'locales.createdDate' },
          { path: 'locales.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'POST' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.BulkCreateLocales',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/bulk/locale/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateLocales;
}

/** Updates multiple locales. */
export function bulkUpdateLocales(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateLocales({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'locales.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'locales.locale.createdDate' },
          { path: 'locales.locale.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'POST' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.BulkUpdateLocales',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/bulk/locale/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateLocales;
}

/**
 * Deletes multiple locales.
 *
 * This method removes only secondary locals from a site. The primary locale can't be deleted.
 *
 * <blockquote class="warning">
 * <strong>Warning:</strong>
 *
 * This method permanently deletes multiple secondary locales and their data from the site.
 * </blockquote>
 */
export function bulkDeleteLocales(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteLocales({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'POST' as any,
      methodFqn: 'wix.multilingual.locales.v2.LocalesService.BulkDeleteLocales',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/bulk/locale/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteLocales;
}

/**
 * Creates and assigns a new primary locale for a site.
 *
 * This method changes the site's primary locale. The process may take time, depending on the amount of content present on the site. When called, this method returns a token, which you can use with the Get New Primary Locale Status method to check the status of the primary locale change.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * - The desired new primary locale can't already exist as a secondary locale on the site. If the locale is already a secondary locale on the site, you must first remove it from the site by calling Delete Locale before changing the primary locale.
 * - This method automatically deletes the original primary locale and transfers its data to the new primary locale.
 *
 * </blockquote>
 */
export function createNewPrimaryLocale(
  payload: object
): RequestOptionsFactory<any> {
  function __createNewPrimaryLocale({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'primaryLocale.createdDate' },
          { path: 'primaryLocale.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.locales.v2.LocalesService.CreateNewPrimaryLocale',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/change-primary',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __createNewPrimaryLocale;
}

/**
 * Retrieves the status of the Create New Primary Locale method.
 *
 * Call this method to see if your primary locale was successfully changed. Specify the token returned by Create New Primary Locale. If the token specified is invalid, a 404 error is returned.
 */
export function getNewPrimaryLocaleStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __getNewPrimaryLocaleStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'GET' as any,
      methodFqn:
        'wix.multilingual.locales.v2.LocalesService.GetNewPrimaryLocaleStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/change-primary',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'finishSuccessfulData.newPrimaryLocale.createdDate' },
              { path: 'finishSuccessfulData.newPrimaryLocale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getNewPrimaryLocaleStatus;
}

/**
 * Sets a locale as the visitor primary locale for the site.
 *
 * Only one locale can be set as the visitor primary locale at a time. When you update the visitor primary locale, site visitors see the new default language on their next page load.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * The specified locale's `visibility` must be set to `"VISIBLE"`.
 *
 * </blockquote>
 */
export function setVisitorPrimaryLocale(
  payload: object
): RequestOptionsFactory<any> {
  function __setVisitorPrimaryLocale({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.multilingual.locales.v2.locale',
      method: 'POST' as any,
      methodFqn:
        'wix.multilingual.locales.v2.LocalesService.SetVisitorPrimaryLocale',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixMultilingualLocalesV2LocalesServiceUrl({
        protoPath: '/v2/locale/set-visitor-primary',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'locale.createdDate' },
              { path: 'locale.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setVisitorPrimaryLocale;
}
